import { useState, useRef } from "react";
import { Upload, X, Loader2, ImagePlus } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function PhotoUploader({ photos = [], onChange, max = 10, label = "Upload photos" }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handleFiles = async (files) => {
    if (!files?.length) return;
    if (photos.length + files.length > max) {
      toast.error(`Maximum ${max} photos allowed`);
      return;
    }
    setUploading(true);
    try {
      const newPhotos = [...photos];
      for (const file of Array.from(files)) {
        const form = new FormData();
        form.append("file", file);
        const { data } = await api.post("/upload/image", form, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        newPhotos.push(`${BACKEND}${data.url}`);
      }
      onChange(newPhotos);
      toast.success(`${files.length} photo${files.length > 1 ? "s" : ""} uploaded`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const remove = (idx) => {
    const next = photos.filter((_, i) => i !== idx);
    onChange(next);
  };

  return (
    <div data-testid="photo-uploader">
      <input
        ref={inputRef}
        type="file"
        multiple
        accept="image/jpeg,image/png,image/webp,image/gif"
        className="hidden"
        onChange={(e) => handleFiles(e.target.files)}
      />

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {photos.map((url, i) => (
          <div key={i} className="relative group aspect-square rounded-lg overflow-hidden bg-stone-100 border border-stone-200">
            <img src={url} alt="" className="w-full h-full object-cover" />
            <button
              type="button"
              onClick={() => remove(i)}
              className="absolute top-1.5 right-1.5 p-1 rounded-full bg-black/60 text-white opacity-0 group-hover:opacity-100 transition"
              data-testid={`remove-photo-${i}`}
            >
              <X className="w-3 h-3" />
            </button>
            {i === 0 && (
              <div className="absolute bottom-1.5 left-1.5 px-1.5 py-0.5 rounded bg-[#064E3B] text-white text-[10px] uppercase tracking-wider font-semibold">
                Cover
              </div>
            )}
          </div>
        ))}

        {photos.length < max && (
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            disabled={uploading}
            className="aspect-square rounded-lg border-2 border-dashed border-stone-300 hover:border-[#064E3B]/50 hover:bg-stone-50 flex flex-col items-center justify-center text-stone-500 transition"
            data-testid="upload-photo-btn"
          >
            {uploading ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <>
                <ImagePlus className="w-5 h-5 mb-1" />
                <span className="text-xs">{label}</span>
              </>
            )}
          </button>
        )}
      </div>
      <p className="text-xs text-stone-500 mt-2">
        {photos.length}/{max} photos. First photo is the cover image. JPG, PNG, WebP up to 10MB each.
      </p>
    </div>
  );
}
